import { z } from "zod";
import { createClient } from "supabase/client.ts";

const supabase = createClient();

// Simple in-memory cache with user context
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 1000; // Prevent memory bloat

function getCacheKey(operation: string, args: any, userContext?: string): string {
  const userPrefix = userContext ? `user:${userContext}:` : 'public:';
  const argsString = JSON.stringify(args, Object.keys(args || {}).sort());
  return `${userPrefix}${operation}:${argsString}`;
}

function getFromCache(key: string, bypassCache = false) {
  if (bypassCache) {
    return null;
  }
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  cache.delete(key);
  return null;
}

function setCache(key: string, data: any) {
  // Simple LRU: remove oldest entries if cache is too large
  if (cache.size >= MAX_CACHE_SIZE) {
    const firstKey = cache.keys().next().value;
    cache.delete(firstKey);
  }
  cache.set(key, { data, timestamp: Date.now() });
}

function invalidateCache(pattern?: string) {
  if (!pattern) {
    cache.clear();
    return;
  }
  Array.from(cache.keys()).forEach(key => {
    if (key.includes(pattern)) {
      cache.delete(key);
    }
  });
}

async function getUserContext(): Promise<string | undefined> {
  try {
    const { data } = await supabase.auth.getUser();
    return data.user?.id;
  } catch {
    return undefined;
  }
}

export type DatabaseOperation<T> = {
  input: z.ZodType<any>;
  handler: (ctx: { db: typeof supabase }, args: any) => Promise<T>;
};

export function defineQuery<T>(operation: DatabaseOperation<T>) {
  return async (args?: z.infer<typeof operation.input>, bypassCache = false) => {
    const validatedInput = operation.input
      ? operation.input.parse(args)
      : undefined;
    
    const userContext = await getUserContext();
    const operationName = operation.handler.toString().slice(0, 50); // Use function signature for uniqueness
    const cacheKey = getCacheKey(operationName, validatedInput, userContext);
    const cached = getFromCache(cacheKey, bypassCache);
    if (cached) return cached;
    
    const ctx = { db: supabase };
    const result = await operation.handler(ctx, validatedInput);
    if (!bypassCache) {
      setCache(cacheKey, result);
    }
    return result;
  };
}

export function defineMutation<T>(operation: DatabaseOperation<T>) {
  return async (args: z.infer<typeof operation.input>) => {
    const validatedInput = operation.input.parse(args);
    const ctx = { db: supabase };
    const result = await operation.handler(ctx, validatedInput);
    
    // Invalidate related cache entries after mutation
    const userContext = await getUserContext();
    const userPrefix = userContext ? `user:${userContext}:` : 'public:';
    invalidateCache(userPrefix);
    
    return result;
  };
}